import type { userAgent } from 'next/server';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import CarouselDisplayTrigger from '@alltrails/amplitude/enums/CarouselDisplayTrigger';
import InstallPromptTrigger from '@alltrails/amplitude/enums/InstallPromptTrigger';
import useApexDomain from '@alltrails/context/hooks/useApexDomain';
import useLoadAppsFlyer from '@alltrails/core/hooks/useLoadAppsFlyer';
import useExperiments from '@alltrails/experiments/hooks/useExperiments';
import useSessionStorage from '@alltrails/session-storage/hooks/useSessionStorage';
import type DeeplinkData from '@alltrails/types/DeeplinkData';
import { InstallPromptType } from '../types';
import { getAFOneLinkConfig } from '../utils/getAFOneLinkConfig';
import getAnalyticsParams from '../utils/getAnalyticsParams';

/*
  useDeeplink creates AppsFlyer OneLink deeplinks for location and trail pages to the app, defaulting to the homepage.
*/

type UseDeepLinkParams = {
  deeplinkData?: DeeplinkData;
  customAppPath?: string;
  isOpen?: boolean;
  promptType?: InstallPromptType;
  // "displayTrigger" represents standalone install prompt triggers or SignupModal triggers
  // It gets added to the AppsFlyer OneLink for web -> app tracking in getAnalyticsParams.ts
  displayTrigger?: CarouselDisplayTrigger | InstallPromptTrigger;
};

const oneLinkFallback = 'https://alltrails.onelink.me/EFvD/hvsmar15';

const useDeeplink = ({ isOpen, deeplinkData, customAppPath, promptType, displayTrigger }: UseDeepLinkParams) => {
  const apexDomain = useApexDomain();
  const browserName = useSelector((state: { browser?: ReturnType<typeof userAgent>['browser'] }) => state.browser?.name);
  const isAFLoaded = useLoadAppsFlyer();
  const experiments = useExperiments();
  const { getValue: getSessionAttribution } = useSessionStorage('sessionAttribution');

  const getDeeplink = useCallback(() => {
    if (!isAFLoaded) {
      return oneLinkFallback;
    }

    let appPath = 'screen/explore';

    if (customAppPath) {
      appPath = customAppPath;
    } else if (deeplinkData) {
      const { pageType, pageId, startDownload } = deeplinkData;

      if (pageType === 'navigate') {
        appPath = `screen/recorder?trailID=${pageId}&start=false`;
      } else {
        appPath = `${pageType}/${pageId}`;
        if (startDownload && pageType === 'trail') {
          appPath += '?startDownload=true';
        }
      }
    }

    const analyticsParams = getAnalyticsParams({ promptType, browserName, displayTrigger, experiments });
    const linkConfig = getAFOneLinkConfig({
      apexDomain,
      deepLinkPath: appPath,
      analyticsParams,
      sessionAttribution: getSessionAttribution()
    });

    const resultUrl = window.AF_SMART_SCRIPT.generateOneLinkURL(linkConfig);

    // Impressions should be triggered after calling generateOneLinkURL, and after a timeout per the AppsFlyer documentation:
    // https://dev.appsflyer.com/hc/docs/dl_smart_script_v2#fire-an-impression
    if (isOpen) {
      setTimeout(() => {
        window.AF_SMART_SCRIPT.fireImpressionsLink();
      }, 1000);
    }

    return resultUrl?.clickURL || oneLinkFallback;
  }, [isAFLoaded, deeplinkData, customAppPath, promptType, browserName, displayTrigger, experiments, apexDomain, getSessionAttribution, isOpen]);

  return getDeeplink;
};

export default useDeeplink;
